import { Group, User, type UserTraits } from '@segment/analytics-next';
import { getCookie, removeCookie } from '@/utils/cookies';
import { getCookieRootDomain } from '@/utils/config';
import { jsonStorage } from '@/utils/json-storage';

interface IdentityClient {
  readonly user: User;
  readonly resetClient?: () => void;
}

function readIdentityCookie(key: string): string | undefined {
  try {
    return getCookie(key);
  } catch {
    return undefined;
  }
}

function removeIdentityCookie(key: string): void {
  for (const domain of new Set([undefined, getCookieRootDomain(), window.location.hostname])) {
    try {
      removeCookie(key, {
        domain,
        path: '/'
      });
    } catch {
      // A blocked cookie must not stop cleanup of the remaining identity stores.
    }
  }
}

function readLegacyUser(): unknown {
  const value = readIdentityCookie('ajs_user');

  if (value === undefined) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function clearSensitiveCache(key: string, property: string): void {
  const value = jsonStorage.getItem<unknown>(key);

  if (isRecord(value) && value[property] !== undefined) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage may be disabled; the shared profile still starts empty below.
    }
  }
}

function readCookieId(key: string): string | null {
  const value = readIdentityCookie(key);

  if (value === undefined || value === '') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);

    return typeof parsed === 'string' || typeof parsed === 'number' ? String(parsed) : null;
  } catch {
    return value;
  }
}

function readStoredId(key: string): string | null {
  const value = jsonStorage.getItem<unknown>(key);

  return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
}

/** Shares visitor/account identity and all person traits between clients. */
export class SharedSegmentIdentity {
  private canonicalUser: User | null = null;
  private readonly clients = new Set<IdentityClient>();
  private userId: string | null = null;
  private anonymousId: string | null = null;
  private profile: UserTraits = {};
  private pendingReset = false;
  private memoryOnly = false;
  private readonly handleStorageChange = (event: StorageEvent): void => {
    if (event.key === null || event.key === 'ajs_user_id' || event.key === 'ajs_anonymous_id' ||
      event.key === 'ajs_user_traits') {
      this.refreshPersistedIdentity(event.key === null || (event.key === 'ajs_user_id' && event.newValue === null));
    }
  };

  get isPrepared(): boolean {
    return this.canonicalUser !== null;
  }

  /** Call only after Functional consent, before constructing either SDK User. */
  prepare(): void {
    if (this.canonicalUser !== null) {
      return;
    }

    /*
     * SDK 1.84 imports ajs_user into each new User. Remove it before another
     * client can restore stale identity. Preserve its safe profile data below.
     */
    const legacyUser = readLegacyUser();

    clearSensitiveCache('ajs_user_traits', 'email');
    clearSensitiveCache('ajs_group_properties', 'confirmation_number');

    const storedTraits = jsonStorage.getItem<unknown>('ajs_user_traits');
    const storedUserId = readStoredId('ajs_user_id') ?? readCookieId('ajs_user_id');
    const storedAnonymousId = readCookieId('ajs_anonymous_id') ?? readStoredId('ajs_anonymous_id');

    removeIdentityCookie('ajs_user');

    this.canonicalUser = new User();

    SharedSegmentIdentity.importLegacyUser(this.canonicalUser, legacyUser, storedTraits);

    // A surviving legacy cookie can overwrite modern stores in the SDK constructor.
    this.userId = storedUserId ?? this.canonicalUser.id() ?? null;
    this.anonymousId = storedAnonymousId ?? this.canonicalUser.anonymousId() ?? null;

    const canonicalTraits = isRecord(storedTraits) ? storedTraits : this.canonicalUser.traits() ?? {};

    this.profile = canonicalTraits.email === undefined ? canonicalTraits : {};
    this.persistIdentity();

    if (this.pendingReset) {
      this.pendingReset = false;
      this.reset();
    }

    window.addEventListener('storage', this.handleStorageChange);
  }

  private static importLegacyUser(user: User, legacyUser: unknown, storedTraits: unknown): void {
    if (user.id() === null) {
      const legacyId = isRecord(legacyUser) ? legacyUser.id : legacyUser;

      if ((typeof legacyId === 'string' && legacyId !== '') || typeof legacyId === 'number') {
        user.id(String(legacyId));
      }
    }

    if (storedTraits === null && isRecord(legacyUser) && isRecord(legacyUser.traits) &&
      legacyUser.traits.email === undefined) {
      user.traits(legacyUser.traits);
    }
  }

  register(user: User, resetClient?: () => void): void {
    this.prepare();
    this.clients.add({
      user,
      resetClient
    });
    this.apply(user);
  }

  getAnonymousId(): string | null {
    this.prepare();
    this.refreshPersistedIdentity();

    return this.anonymousId;
  }

  setAnonymousId(id: string): void {
    this.prepare();
    this.anonymousId = id;
    this.persistIdentity();
    this.synchronize();
  }

  /** Align stale SDK memory without emitting identify events to another source. */
  synchronize(anonymousId?: string): void {
    this.prepare();
    this.refreshPersistedIdentity();

    if (anonymousId !== undefined && anonymousId !== '') {
      this.anonymousId = anonymousId;
    }

    this.persistIdentity();

    for (const { user } of this.clients) {
      this.apply(user);
    }
  }

  identify(userId: string | null, traits: UserTraits = {}, anonymousId?: string): void {
    this.prepare();
    this.refreshPersistedIdentity();

    if (this.canonicalUser === null) {
      return;
    }

    const changingAccount = userId !== null && this.userId !== null && userId !== this.userId;

    this.profile = {
      ...changingAccount ? {} : this.profile,
      ...traits
    };

    // Match SDK identify(): an empty ID updates traits without replacing account identity.
    if (userId !== null && userId !== '') {
      this.userId = userId;

      if (changingAccount) {
        this.anonymousId = new User({ persist: false }).anonymousId() ?? null;
      }
    }

    this.persistIdentity();

    for (const { user } of this.clients) {
      this.apply(user);
    }

    this.synchronize(anonymousId);
  }

  reset(): void {
    if (!this.isPrepared) {
      this.pendingReset = true;

      return;
    }

    for (const { user, resetClient } of this.clients) {
      try {
        if (resetClient === undefined) {
          user.reset();
        } else {
          resetClient();
        }
      } catch {
        // One unavailable source must not stop global logout of the other clients.
        user.reset();
      }
    }

    removeIdentityCookie('ajs_user');
    removeIdentityCookie('_sio');

    new Group().reset();
    new Group({
      cookie: { key: 'ajs_website_group_id' },
      localStorage: { key: 'ajs_website_group_properties' }
    }).reset();
    new User().reset();
    this.userId = null;
    this.profile = {};
    this.anonymousId = new User({ persist: false }).anonymousId() ?? null;
    this.persistIdentity();
    this.synchronize();
  }

  private persistIdentity(): void {
    if (this.canonicalUser === null) {
      return;
    }

    this.apply(this.canonicalUser);

    if (this.memoryOnly) {
      return;
    }

    try {
      if (this.canonicalUser.id() === this.userId && this.canonicalUser.anonymousId() === this.anonymousId &&
        JSON.stringify(this.canonicalUser.traits()) === JSON.stringify(this.profile)) {
        return;
      }
    } catch {
      // Treat unreadable state like a failed write below.
    }

    /*
     * Readable, unwritable cookies can beat SDK memory even after reset. Keep the
     * intended person authoritative for this page when persistence disagrees.
     */
    this.memoryOnly = true;
    this.canonicalUser = new User({ persist: false });
    this.apply(this.canonicalUser);
  }

  private apply(user: User): void {
    // Set anonymousId last: SDK id() can clear it when changing accounts.
    user.id(this.userId);
    user.anonymousId(this.anonymousId);
    user.traits(this.profile);
  }

  private refreshPersistedIdentity(explicitLogout = false): void {
    if (this.memoryOnly) {
      return;
    }

    const storedUserId = readStoredId('ajs_user_id') ?? readCookieId('ajs_user_id');
    const storedAnonymousId = readCookieId('ajs_anonymous_id') ?? readStoredId('ajs_anonymous_id');
    const visitorChanged = storedAnonymousId !== null && storedAnonymousId !== this.anonymousId;
    const accountChanged = storedUserId !== null && storedUserId !== this.userId;
    const loggedOut = storedUserId === null && (visitorChanged || explicitLogout);
    const storedTraits = jsonStorage.getItem<unknown>('ajs_user_traits');

    if (accountChanged || loggedOut || visitorChanged) {
      this.userId = storedUserId;
      this.profile = {};
    }

    if (storedAnonymousId !== null) {
      this.anonymousId = storedAnonymousId;
    }

    if (isRecord(storedTraits) && !loggedOut) {
      this.profile = {
        ...this.profile,
        ...storedTraits
      };
    }
  }
}

export const sharedSegmentIdentity = new SharedSegmentIdentity();
